/**
 * Pure domain module for the Uploads Simulator composer.
 *
 * No React, no data layer — everything here is a plain function over plain
 * objects so the unit tests in `tests/sorModel.test.js` can exercise it
 * without any component rendering. All money values are authored in DOLLARS;
 * the service normalizes to cents server-side (`request.sor_effective`).
 */

/** Server cap on the serialized `sor` multipart field. */
export const SOR_MAX_BYTES = 64 * 1024;

/** Max rows the authored-SOR schema accepts. */
export const MAX_PAYSTUB_ROWS = 24;
export const MAX_ATTEMPT_ROWS = 25;

/** The ONLY keys the service accepts inside `settings`. */
export const SETTINGS_KEYS = Object.freeze([
    "manual_flow_origin",
    "onboarding_complete",
    "verified",
    "salary_exempt",
    "social_security_administration",
    "member_id",
    "linked_account_id",
    "supports_time_and_attendance",
    "type"
]);

const SYNC_STATUS_VALUES = Object.freeze(["IN_PROGRESS", "PARTIAL", "RETRY", "FAILED"]);

/**
 * A fully-shaped empty SOR so every composer input stays controlled.
 * `serializeSor` strips the emptiness back out before upload.
 * @returns {object}
 */
export function buildDefaultSor() {
    return {
        identity: {
            full_name: "",
            date_of_birth: "",
            last_four_ssn: "",
            emails: [],
            phone_numbers: [],
            address: {line1: "", line2: "", city: "", state: "", postal_code: "", country: "US", raw: ""}
        },
        employment: {employer_name: "", status: "", title: "", start_date: ""},
        income: {compensation_unit: "hourly", compensation_amount: null, hourly_wage: null, pay_frequency: "", currency: "USD", employer_name: ""},
        recent_paystubs: [],
        direct_deposit_account: {bank_name: "", routing_number: "", account_last_four: "", type: "", source: "real"},
        recent_intake_attempts: [],
        core_owner: {fullName: "", address: {street: "", city: "", state: "", zip: "", country: "US"}, ssn: "", email: "", phone: "", birthdate: ""},
        settings: {},
        sync_status: null,
        no_sync: false,
        fi_overrides: {name: "", routing_number: "", synonyms: []}
    };
}

/** Blank rows the composer appends when the admin adds a new entry. */
export function buildEmptyPaystubRow() {
    return {
        pay_date: "",
        pay_period_start: "",
        pay_period_end: "",
        gross_pay_amount: null,
        net_pay_amount: null,
        check_amount: null,
        employer_name: "",
        employee_name: "",
        source: "real"
    };
}

export function buildEmptyAttemptRow() {
    return {accepted: false, reason: "", minutes_ago: 60, batch_id: "", status: "completed"};
}

export function buildEmptyPhoneRow() {
    return {type: "mobile", value: ""};
}

function isPlainObject(value) {
    return !!value && typeof value === "object" && !Array.isArray(value);
}

/**
 * Deep-merge an externally-sourced SOR (server preset, localStorage, older
 * schema) onto `buildDefaultSor()` so every field the composer binds to
 * exists. Extra keys the incoming SOR carries (e.g. paystub `earnings`) are
 * preserved untouched.
 *
 * @param {object | null | undefined} raw
 * @returns {object}
 */
export function normalizeSor(raw) {
    const base = buildDefaultSor();
    if (!isPlainObject(raw)) return base;

    const mergeSection = (defaults, incoming) => {
        if (incoming === null) return defaults;
        if (!isPlainObject(incoming)) return defaults;
        const merged = {...defaults, ...incoming};
        Object.keys(defaults).forEach(key => {
            if (isPlainObject(defaults[key])) merged[key] = mergeSection(defaults[key], incoming[key]);
        });
        return merged;
    };

    return {
        ...base,
        ...raw,
        identity: mergeSection(base.identity, raw.identity),
        employment: mergeSection(base.employment, raw.employment),
        income: mergeSection(base.income, raw.income),
        direct_deposit_account: mergeSection(base.direct_deposit_account, raw.direct_deposit_account),
        core_owner: mergeSection(base.core_owner, raw.core_owner),
        fi_overrides: mergeSection(base.fi_overrides, raw.fi_overrides),
        settings: isPlainObject(raw.settings) ? raw.settings : {},
        recent_paystubs: Array.isArray(raw.recent_paystubs) ? raw.recent_paystubs.map(row => ({...buildEmptyPaystubRow(), ...(isPlainObject(row) ? row : {})})) : [],
        recent_intake_attempts: Array.isArray(raw.recent_intake_attempts) ? raw.recent_intake_attempts.map(row => ({...buildEmptyAttemptRow(), ...(isPlainObject(row) ? row : {})})) : [],
        sync_status: SYNC_STATUS_VALUES.includes(raw.sync_status) ? raw.sync_status : null,
        no_sync: raw.no_sync === true
    };
}

/**
 * Force `settings.onboarding_complete` off. The composer defaults the flag
 * off everywhere it applies a SOR the admin did not author by hand — the
 * Full Manual Flow tab on entry (it simulates the onboarding batch itself)
 * and every member SOR loaded from the core (`buildMemberSorApplication`),
 * where onboarded members always carry it as true. The composer switch is
 * the only way to turn it on. Returns the input untouched (same reference)
 * when the flag is already off/absent.
 *
 * @param {object} sor
 * @returns {object}
 */
export function stripOnboardingComplete(sor) {
    if (!isPlainObject(sor?.settings) || !sor.settings.onboarding_complete) return sor;
    const settings = {...sor.settings};
    delete settings.onboarding_complete;
    return {...sor, settings};
}

/**
 * Mode-entry defaults, applied whenever the composer lands on a mode — tab
 * switch and every programmatic SOR application (last-SOR restore, server
 * sample, saved preset). Currently: the Full Manual Flow always starts with
 * `onboarding_complete` off (see {@link stripOnboardingComplete}). Returns
 * the input untouched (same reference) for every other mode.
 *
 * @param {object} sor
 * @param {string} mode `paystub | shifts | direct_deposit | manual_batch`
 * @returns {object}
 */
export function applyModeEntryDefaults(sor, mode) {
    if (mode !== "manual_batch") return sor;
    return stripOnboardingComplete(sor);
}

function isBlankNumber(value) {
    return value === null || value === undefined || value === "";
}

function isFiniteNumber(value) {
    return typeof value === "number" && Number.isFinite(value);
}

/**
 * Validate the authored SOR for a given simulator mode.
 *
 * Hard rules only — the service tolerates sparse SOR data by design (that is
 * the point of the simulator), so validation focuses on values that would be
 * rejected server-side or silently corrupt the demo:
 *  - any authored paystub row must carry `pay_period_end`
 *  - every money/number field must be a finite number (or blank)
 *  - attempt history `minutes_ago` must be a number >= 0
 *  - identity last-4 / routing numbers must match their fixed digit shapes
 *  - row caps (24 paystubs / 25 attempts) and the 64KB serialized cap
 *
 * @param {object} sor authored SOR
 * @param {string} mode `paystub | shifts | direct_deposit | manual_batch`
 * @returns {{ok: boolean, errors: Record<string, string>}} errors keyed by dot-path
 */
export function validateSor(sor, mode) {
    const errors = {};

    if (!isPlainObject(sor)) {
        return {ok: false, errors: {sor: "SOR must be an object."}};
    }

    const checkNumber = (path, value, {min = null} = {}) => {
        if (isBlankNumber(value)) return;
        if (!isFiniteNumber(value)) {
            errors[path] = "Must be a finite number.";
            return;
        }
        if (min !== null && value < min) {
            errors[path] = `Must be at least ${min}.`;
        }
    };

    const lastFour = sor.identity?.last_four_ssn;
    if (lastFour && !/^\d{4}$/.test(String(lastFour))) {
        errors["identity.last_four_ssn"] = "Last 4 of SSN must be exactly 4 digits.";
    }

    checkNumber("income.compensation_amount", sor.income?.compensation_amount, {min: 0});
    checkNumber("income.hourly_wage", sor.income?.hourly_wage, {min: 0});

    const paystubs = Array.isArray(sor.recent_paystubs) ? sor.recent_paystubs : [];
    if (paystubs.length > MAX_PAYSTUB_ROWS) {
        errors.recent_paystubs = `At most ${MAX_PAYSTUB_ROWS} paystub rows are allowed.`;
    }
    paystubs.forEach((row, index) => {
        if (!row?.pay_period_end) {
            errors[`recent_paystubs.${index}.pay_period_end`] = "Pay period end is required on every paystub row.";
        }
        checkNumber(`recent_paystubs.${index}.gross_pay_amount`, row?.gross_pay_amount, {min: 0});
        checkNumber(`recent_paystubs.${index}.net_pay_amount`, row?.net_pay_amount, {min: 0});
        checkNumber(`recent_paystubs.${index}.check_amount`, row?.check_amount, {min: 0});
    });

    const attempts = Array.isArray(sor.recent_intake_attempts) ? sor.recent_intake_attempts : [];
    if (attempts.length > MAX_ATTEMPT_ROWS) {
        errors.recent_intake_attempts = `At most ${MAX_ATTEMPT_ROWS} attempt rows are allowed.`;
    }
    attempts.forEach((row, index) => {
        const minutesAgo = row?.minutes_ago;
        if (isBlankNumber(minutesAgo) || !isFiniteNumber(minutesAgo) || minutesAgo < 0) {
            errors[`recent_intake_attempts.${index}.minutes_ago`] = "Minutes ago must be a number >= 0.";
        }
    });

    const routing = sor.direct_deposit_account?.routing_number;
    if (routing && !/^\d{9}$/.test(String(routing))) {
        errors["direct_deposit_account.routing_number"] = "Routing number must be exactly 9 digits.";
    }
    const ddLastFour = sor.direct_deposit_account?.account_last_four;
    if (ddLastFour && !/^\d{4}$/.test(String(ddLastFour))) {
        errors["direct_deposit_account.account_last_four"] = "Account last 4 must be exactly 4 digits.";
    }
    const fiRouting = sor.fi_overrides?.routing_number;
    if (fiRouting && !/^\d{9}$/.test(String(fiRouting))) {
        errors["fi_overrides.routing_number"] = "FI routing number must be exactly 9 digits.";
    }

    // Serialized size cap — checked last so the message reflects what actually
    // uploads. Measured in UTF-8 BYTES to match the server's Buffer.byteLength
    // check (string .length counts UTF-16 code units and undercounts multibyte
    // text by up to 3x).
    try {
        const bytes = utf8ByteLength(JSON.stringify(serializeSor(sor)));
        if (bytes > SOR_MAX_BYTES) {
            errors.sor = `Serialized SOR is ${Math.round(bytes / 1024)}KB — the server caps it at 64KB.`;
        }
    } catch (error) {
        errors.sor = "SOR could not be serialized to JSON.";
    }

    return {ok: Object.keys(errors).length === 0, errors};
}

/**
 * UTF-8 byte length of a string, mirroring the server's
 * `Buffer.byteLength(raw, "utf8")` — pure JS so it behaves identically in
 * browsers and the jsdom test environment (which lacks TextEncoder).
 */
function utf8ByteLength(text) {
    let bytes = 0;
    for (const char of text) {
        const code = char.codePointAt(0);
        bytes += code <= 0x7f ? 1 : code <= 0x7ff ? 2 : code <= 0xffff ? 3 : 4;
    }
    return bytes;
}

function cleanValue(value) {
    if (typeof value === "string") {
        const trimmed = value.trim();
        return trimmed === "" ? undefined : trimmed;
    }
    if (typeof value === "number") return Number.isFinite(value) ? value : undefined;
    if (typeof value === "boolean") return value;
    if (Array.isArray(value)) {
        const cleaned = value.map(cleanValue).filter(entry => entry !== undefined);
        return cleaned.length ? cleaned : undefined;
    }
    if (isPlainObject(value)) {
        const cleaned = {};
        Object.keys(value).forEach(key => {
            const entry = cleanValue(value[key]);
            if (entry !== undefined) cleaned[key] = entry;
        });
        return Object.keys(cleaned).length ? cleaned : undefined;
    }
    return undefined;
}

/**
 * Convert the fully-shaped composer SOR into the sparse payload the service
 * expects: empty strings/objects stripped, empty sections collapsed to null,
 * empty arrays dropped entirely, `settings` reduced to its whitelist, and the
 * `no_sync` / `sync_status` toggles included only when meaningful.
 *
 * @param {object} sor
 * @returns {object}
 */
export function serializeSor(sor) {
    if (!isPlainObject(sor)) return {};

    const out = {};

    ["identity", "employment", "income", "direct_deposit_account", "core_owner", "fi_overrides"].forEach(section => {
        const cleaned = cleanValue(sor[section]);
        out[section] = cleaned ?? null;
    });

    // Country defaults ("US") shouldn't force an otherwise-empty section alive.
    ["identity", "core_owner"].forEach(section => {
        const address = out[section]?.address;
        if (address && Object.keys(address).length === 1 && address.country) {
            const rest = {...out[section]};
            delete rest.address;
            out[section] = Object.keys(rest).length ? rest : null;
        }
    });
    if (
        out.income &&
        Object.keys(out.income).length <= 2 &&
        out.income.currency === "USD" &&
        out.income.compensation_unit &&
        isBlankNumber(sor.income?.compensation_amount) &&
        isBlankNumber(sor.income?.hourly_wage)
    ) {
        // Only the two defaults survived cleaning — nothing was authored.
        out.income = null;
    }
    if (out.direct_deposit_account && Object.keys(out.direct_deposit_account).length === 1 && out.direct_deposit_account.source) {
        out.direct_deposit_account = null;
    }

    const paystubs = cleanValue(sor.recent_paystubs);
    if (paystubs) out.recent_paystubs = paystubs;

    const attempts = cleanValue(sor.recent_intake_attempts);
    if (attempts) out.recent_intake_attempts = attempts;

    if (isPlainObject(sor.settings)) {
        const settings = {};
        SETTINGS_KEYS.forEach(key => {
            const value = cleanValue(sor.settings[key]);
            if (value !== undefined) settings[key] = value;
        });
        if (Object.keys(settings).length) out.settings = settings;
    }

    if (SYNC_STATUS_VALUES.includes(sor.sync_status)) out.sync_status = sor.sync_status;
    if (sor.no_sync === true) out.no_sync = true;

    // Member-loaded SORs carry the account row's contact fields — the server's
    // normalizer consumes them for the fabricated AccountInfo, so dropping
    // them here would break the exact member round trip.
    ["account_name", "account_email", "account_phone"].forEach(key => {
        const value = cleanValue(sor[key]);
        if (value !== undefined) out[key] = value;
    });

    return out;
}

function toDate(value) {
    if (!value) return null;
    const date = new Date(`${String(value).slice(0, 10)}T00:00:00Z`);
    return Number.isNaN(date.getTime()) ? null : date;
}

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Shape `batch_analysis` (+ the post-state paystub rows, used to map
 * document ids onto period ends) into the chip/link/gap model the
 * `ContiguityStrip` renders.
 *
 * Gap detection: with no pay frequency in the report, the cadence is inferred
 * from the median day-delta between successive period ends; a delta that
 * clearly exceeds the cadence (1.5x median and 3+ days over) is flagged as a
 * gap. Two chips can never flag a gap (a single delta IS the cadence).
 *
 * @param {object | null | undefined} batchAnalysis report `batch_analysis`
 * @param {object[] | null | undefined} dbPaystubs report `db_post_state.paystubs`
 * @returns {{hasData: boolean, chips: object[], contiguousCount: number | null, requiredCount: number | null}}
 */
export function buildContiguityModel(batchAnalysis, dbPaystubs) {
    const periodEnds = Array.isArray(batchAnalysis?.period_ends) ? batchAnalysis.period_ends.filter(Boolean) : [];
    const excluded = Array.isArray(batchAnalysis?.excluded) ? batchAnalysis.excluded.filter(Boolean) : [];
    const qualifyingIds = new Set(Array.isArray(batchAnalysis?.qualifying_document_ids) ? batchAnalysis.qualifying_document_ids.filter(Boolean) : []);

    const documentIdByPeriodEnd = {};
    (Array.isArray(dbPaystubs) ? dbPaystubs : []).forEach(row => {
        const periodEnd = row?.pay_period_end;
        const documentId = row?.document_id ?? row?.documentId;
        if (periodEnd && documentId && !(periodEnd in documentIdByPeriodEnd)) {
            documentIdByPeriodEnd[periodEnd] = documentId;
        }
    });

    const excludedByPeriodEnd = {};
    excluded.forEach(entry => {
        if (entry?.pay_period_end) excludedByPeriodEnd[entry.pay_period_end] = entry.reason ?? "excluded";
    });

    const allPeriodEnds = [...new Set([...periodEnds, ...excluded.map(entry => entry?.pay_period_end).filter(Boolean)])];
    const chips = allPeriodEnds
        .map(periodEnd => ({periodEnd, date: toDate(periodEnd)}))
        .sort((a, b) => {
            if (a.date && b.date) return a.date - b.date;
            return String(a.periodEnd).localeCompare(String(b.periodEnd));
        })
        .map(({periodEnd, date}) => {
            const documentId = documentIdByPeriodEnd[periodEnd] ?? null;
            const isExcluded = periodEnd in excludedByPeriodEnd;
            const qualifying = qualifyingIds.size > 0 && (documentId ? qualifyingIds.has(documentId) : !isExcluded);
            return {
                key: periodEnd,
                periodEnd,
                date,
                documentId,
                excluded: isExcluded,
                excludedReason: isExcluded ? excludedByPeriodEnd[periodEnd] : null,
                qualifying: !isExcluded && qualifying,
                gapAfter: false
            };
        });

    const deltas = [];
    for (let index = 0; index < chips.length - 1; index += 1) {
        const current = chips[index].date;
        const next = chips[index + 1].date;
        deltas.push(current && next ? Math.round((next - current) / DAY_MS) : null);
    }
    const knownDeltas = deltas.filter(delta => typeof delta === "number" && delta > 0).sort((a, b) => a - b);
    if (knownDeltas.length >= 2) {
        // Lower-middle median: with exactly 2 deltas (the standard 3-chip
        // batch) the upper-middle picks the LARGER delta, making the
        // `delta > median * 1.5` gap test unsatisfiable — a real cadence
        // break would render as a solid contiguous link.
        const median = knownDeltas[Math.floor((knownDeltas.length - 1) / 2)];
        deltas.forEach((delta, index) => {
            if (typeof delta === "number" && delta > median * 1.5 && delta - median >= 3) {
                chips[index].gapAfter = true;
            }
        });
    }

    return {
        hasData: chips.length > 0,
        chips,
        contiguousCount: typeof batchAnalysis?.contiguous_count === "number" ? batchAnalysis.contiguous_count : null,
        requiredCount: typeof batchAnalysis?.required_count === "number" ? batchAnalysis.required_count : null
    };
}

/**
 * Map any error thrown by the simulate/poll data-layer calls into the
 * `{title, detail, kind}` shape the results panel renders. `cancelled` is a
 * silent kind — the controller drops it without surfacing anything.
 *
 * @param {unknown} error
 * @returns {{kind: string, title: string, detail: string}}
 */
export function mapSimulateError(error) {
    if (error?.code === "ERR_CANCELED" || error?.name === "CanceledError" || error?.name === "AbortError") {
        return {kind: "cancelled", title: "", detail: ""};
    }
    if (error?.code === "ECONNABORTED") {
        return {kind: "timeout", title: "Upload timed out", detail: "The request took longer than 60 seconds to reach the server. Check the file sizes and your connection, then try again."};
    }

    const status = error?.response?.status;
    const serverDetail = typeof error?.response?.data?.error === "string" ? error.response.data.error : null;

    if (status === 400 || status === 422) {
        return {kind: "validation", title: "Request rejected by the server", detail: serverDetail || "The simulation payload failed server-side validation."};
    }
    if (status === 403) {
        return {kind: "access", title: "Access denied", detail: serverDetail || "You do not have access to run intake simulations for this FI (or manual uploads are disabled for it)."};
    }
    if (status === 404) {
        return {kind: "expired", title: "Run expired", detail: serverDetail || "Simulation results expire 15 minutes after they complete (nothing persists server-side). Run the simulation again."};
    }
    if (status === 413) {
        return {kind: "size", title: "File too large", detail: serverDetail || "Each document must be 5MB or smaller."};
    }
    if (status === 429) {
        return {
            kind: "concurrency",
            title: "Too many simulations",
            detail: serverDetail || "Concurrency caps are strict: 2 runs per user, 4 globally, 10 starts per minute. Wait for the running simulations to finish and try again."
        };
    }
    if (typeof status === "number" && status >= 500) {
        return {kind: "server", title: "Simulation service error", detail: serverDetail || "The service hit an unexpected error. Nothing persisted — try again."};
    }
    if (error?.request && !error?.response) {
        return {kind: "network", title: "Network error", detail: "Could not reach the service. Check your connection and try again."};
    }
    return {kind: "unknown", title: "Simulation failed", detail: serverDetail || error?.message || "Unexpected error."};
}
